import React from 'react';
import { motion } from 'framer-motion';
import { Pencil, Trash2, Star, Share2, Globe } from 'lucide-react';
import { formatDate } from '../lib/utils';
import type { Note, ViewMode } from '../types';
import Button from './ui/Button';
import { cardHover, springTransition } from '../lib/animations';
import AvatarTooltip from './ui/AvatarTooltip';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface NoteCardProps {
  note: Note;
  viewMode: ViewMode;
  onEdit: (note: Note) => void;
  onDelete: (noteId: string) => void;
  onShare: (note: Note) => void;
  user?: {
    name: string;
    avatar_url?: string;
  };
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  viewMode,
  onEdit,
  onDelete,
  onShare,
  user,
}) => {
  const [tags, setTags] = React.useState<any[]>([]);

  React.useEffect(() => {
    const fetchTags = async () => {
      if (!note.tags) return;

      // Filter out non-UUID tag IDs
      const validTagIds = note.tags.filter((tagId) => {
        // Basic UUID check (more robust validation might be needed)
        return (
          typeof tagId === 'string' &&
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
            tagId
          )
        );
      });

      try {
        const { data: customTags, error } = await supabase
          .from('tags')
          .select('*')
          .in('id', validTagIds);

        if (error) {
          console.error('Error fetching tags (Supabase error):', error); // Log Supabase error
          throw error; // Re-throw to trigger the catch block
        }
        setTags(customTags || []);
      } catch (error: any) {
        console.error('Error fetching tags (Network or other):', error); // More general error
        setTags([]);
      }
    };

    fetchTags();
  }, [note.tags]);

  const cardClassName =
    viewMode === 'grid' ? 'h-full' : 'flex gap-4 items-start';

  const contentClassName = viewMode === 'grid' ? '' : 'flex-1';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      variants={cardHover}
      whileHover="hover"
      transition={springTransition}
      className={`group relative overflow-hidden rounded-xl border bg-card p-4 hover:shadow-lg transition-shadow cursor-pointer ${cardClassName}`}
    >
      <div className={contentClassName}>
        <div className="flex items-start justify-between mb-2">
          <Link to={`/notes/${note.id}`}>
            <motion.h3
              className="text-lg font-semibold line-clamp-1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {note.title}
            </motion.h3>
          </Link>
          <div className="flex gap-1">
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShare(note);
                }}
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {note.is_public ? (
                  <Globe className="h-4 w-4 text-primary" />
                ) : (
                  <Share2 className="h-4 h-4" />
                )}
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(note);
                }}
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(note.id);
                }}
                className="h-8 w-8 p-0 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </motion.div>
          </div>
        </div>

        <Link to={`/notes/${note.id}`}>
          <div className="min-h-[100px] flex flex-col">
            <motion.p
              className="text-muted-foreground line-clamp-3 mb-4 flex-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {note.content}
            </motion.p>

            <div className="mt-auto">
              <motion.div
                className="flex flex-wrap gap-2 mb-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {tags.map((tag, index) => (
                  <motion.span
                    key={tag.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * index }}
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tag.name}
                  </motion.span>
                ))}
              </motion.div>

              <motion.div
                className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center gap-2">
                  {user && (
                    <AvatarTooltip
                      name={user.name}
                      url={user.avatar_url}
                      size="sm"
                    />
                  )}
                  <time>{formatDate(note.updatedAt)}</time>
                </div>
                {note.isFavorite && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.2 }}
                  >
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </Link>
      </div>
    </motion.div>
  );
};

export default NoteCard;